<template lang="pug">
    layout.contentTransparent(fluid)

        // Title
        title-component(
            :title="$t('users.list.title')"
            slot="title"
            type="h2"
        )

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs12 lg3)
                    div.c-form

                        // Search field
                        v-text-field(
                            :label="$t('fields.search')"
                            v-model="search"
                            append-icon="search"
                            class="search-input"
                            outline
                        )
            separator-component.overlay

            v-layout(row wrap)
                v-flex(xs12 v-if="filterParams")

                    // Users list (Mobile)
                    v-data-table.c-table(
                        v-if="isMobile"
                        :headers-length="7"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(
                            v-slot:items="props"
                        )
                            tr(@click="props.expanded = !props.expanded")
                                list-mobile(
                                    :item="props.item"
                                    :expanded="props.expanded"
                                    @removed="resetPagination"
                                )

                        template(slot="expand" slot-scope="props")
                            tr
                                td(colspan="6")
                                    expand-card(:item="props.item" :customFields="model")



                    v-data-table.c-table.visibleHeader(
                        v-if="!isMobile"
                        :headers="headers"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(slot="headerCell" slot-scope="props")
                            div.text-left(v-if="props.header.filter")
                                filter-component(
                                    :items="filterItems(props.header.filter)"
                                    :name="props.header.text"
                                    :callbackFunction="filtersCallback"
                                    :filterName="props.header.value"
                                )

                            span(v-else) {{ `${props.header.text}  ` }}

                        template(slot="items" slot-scope="props")
                            list-item(:item="props.item" :customFields="model" @removed="resetPagination")

        controls-mobile(
            v-if="isMobile"
            :filterHandler="filtersCallback"
            :filters="filterParams"
        )
</template>

<script>
    import Layout from '../../Layout/Layout';
    import ListItem from './ListItem';
    import ListMobile from './ListItemMobile';
    import ExpandCard from './ExpandCard';
    import ControlsMobile from './ControlsMobile';
    import VueTypes from 'vue-types';
    import users from '~api/users';
    import Button from '~components/Button';
    import Filter from '~components/Filter';
    import Separator from '~components/Separator';
    import Title from '~components/Title';
    import UploadButton from '~components/UploadButton';
    import { mapFields } from 'vuex-map-fields';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            Layout,
            ListItem,
            ListMobile,
            ExpandCard,
            UploadButton,
            ControlsMobile,
            TitleComponent: Title,
            ButtonComponent: Button,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        props: {
            id: VueTypes.string.def('')
        },
        data() {
            return {
                filters: {
                    businessName: null
                },
                headers: this.$t('goals.tableHeaders'),
                filterParams: null,
                items: [],
                total: 0,
                rowsPerPage: [10],
                pagination: {
                    descending: true
                },
                params: null,
                search: null,
                timeout: null,
                routeInitialized: false
            };
        },
        computed: {
            ...mapFields('document', {
                model: 'fields'
            }),
            ...mapGetters('document', ['isLoading']),
            ...mapGetters('auth', ['showDepartment', 'isChampion', 'isAdmin']),

            /**
             * Check screen resolution
             */
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },

            /**
             * Show controls by role
             */
            showControls() {
                return !!(this.isChampion || this.isAdmin);
            },

            noUsers() {
                return this.items.length === 0;
            }
        },
        watch: {
            /**
             * Watch search and set pagination param on change
             */
            search(value) {
                clearTimeout(this.timeout);

                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);
            },

            /**
             * Watch pagination and update managers data on change
             */
            pagination: {
                async handler(paginationData) {
                    let paginationBlock = document.querySelector(
                        '.v-datatable__actions__range-controls'
                    );

                    if (!this.routeInitialized) {
                        this.pagination.sortBy = null;
                        return;
                    }

                    // Collect params.
                    this.params = {
                        page: paginationData.page,
                        search: paginationData.search,
                        sortBy: paginationData.sortBy,
                        orderBy: paginationData.descending ? 'desc' : 'asc',
                        withStats: true
                    };

                    // Collect filter params
                    Object.keys(this.filters).map(key => {
                        if (this.filters[key] !== null)
                            return (this.params[key] = this.filters[key]);
                    });

                    // Fetch data from API.
                    let resp;
                    if (this.id && this.id.length) {
                        this.params.businessId = this.id;
                    }
                    resp = await users.list(this.params);

                    const { data } = resp;

                    this.items = data.items;
                    this.total =
                        data.meta && data.meta.total ? data.meta.total : 0;

                    if (paginationBlock) {
                        if (this.total <= this.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }
                },
                deep: true
            }
        },
        created() {
            if ((this.id && this.id.length) || this.isChampion) {
                this.readFields(this.id);
            } else {
                this.model = [];
            }
        },

        // Retrieve Users.
        async beforeRouteEnter(to, from, next) {
            // Fetch data from API.
            let resp;

            if (to.name === 'dashboard.users.by.business') {
                resp = await users.list({
                    businessId: to.params.id,
                    withStats: true
                });
            } else {
                resp = await users.list({ withStats: true });
            }

            const { data } = resp;

            next(vm => {
                let paginationBlock = document.querySelector(
                    '.v-datatable__actions__range-controls'
                );

                vm.items = data.items;
                vm.total = data.meta && data.meta.total ? data.meta.total : 0;

                vm.filterParams = { ...data.filters };

                vm.pagination = {
                    page: data.meta && data.meta.page ? data.meta.page : 0,
                    rowsPerPage:
                        data.meta && data.meta.limit ? data.meta.limit : 0
                };

                if (paginationBlock) {
                    if (vm.total <= vm.pagination.rowsPerPage) {
                        paginationBlock.style.display = 'none';
                    } else {
                        paginationBlock.style.display = '';
                    }
                }

                vm.$nextTick(() => {
                    vm.routeInitialized = true;
                });
            });
        },
        methods: {
            ...mapActions('document', [
                'importUsers',
                'exportUsers',
                'readFields'
            ]),

            /**
             * Mobile sorting handler
             */
            handleMobileSort(name, e) {
                this.pagination.sortBy = name;
                this.pagination.descending = e;
                this.resetPagination();
            },

            /**
             * Export users with selected params
             */
            exportUsersByParams() {
                this.exportUsers(this.params);
            },

            /**
             * Items for filter
             */
            filterItems(filter) {
                if (typeof filter === 'object') return filter;
                const customFilters = this.filterParams[filter].map(item => {
                    return { title: item, value: item };
                });

                return [
                    {
                        title: 'All',
                        value: null
                    },
                    ...customFilters
                ];
            },

            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Redirect to save custom fields and create template page
             */
            createTemplate() {
                this.$router.push({ name: 'dashboard.document.crete' });
            },

            /**
             * Redirect to save custom fields and create template page
             */
            createUser() {
                this.$router.push({ name: 'dashboard.users.create' });
            },

            /**
             * Get filter param on change status filter
             * @param {string} e
             */
            filtersCallback(e) {
                this.filters[e.name] = e.value;
                this.resetPagination();
            },

            /**
             * Change sort in table column
             * @param column
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            },

            /**
             * Upload document with users data
             * @param e
             * @returns {Promise<void>}
             */
            async uploadDocument(e) {
                let formData = new FormData();
                formData.append('name', e.name);
                formData.append('file', e);

                const importResp = await this.importUsers(formData);

                if (importResp) {
                    const resp = await users.list();
                    this.items = resp.data.items;
                    this.filterParams = { ...resp.data.filters };

                    if (this.isChampion) {
                        this.updateUserData(
                            this.filterParams.departments || []
                        );
                    }

                    this.resetPagination();
                }
            },

            /**
             * Update user data in local storage
             *
             * @param departments
             */
            updateUserData(departments) {
                let user = JSON.parse(window.localStorage.getItem('user'));

                if (user.business) {
                    user.business.userDepartments = departments;
                }
                window.localStorage.setItem('user', JSON.stringify(user));
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'

    .controlsWrapper
        display: flex
        justify-content: flex-end

        & > div
            flex: 1 1 33.3%

            &:not(:last-child)
                padding-right: 16px

        .controlsButton
            width: 100%
            margin: 0 0 8px

    .text-left
        text-align: left

    .ceilText
        display: inline-block

    .createButtonWrapper
        .btn
            margin-top: 0
            margin-bottom: 0

    .iconImport
    .iconExport
        font-size: 18px

    .iconImport
        transform: rotate(-90deg)

    .iconExport
        transform: rotate(90deg)

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .controlsWrapper
            flex-direction: column

            & > div:not(:last-child)
                padding-right: 0
                margin-bottom: 16px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .createButtonWrapper
            display: block

            .btn
                width: 100%
                margin-bottom: 16px
                margin-right: 0

        .controlsWrapper
            & > div:not(:last-child)
                margin-bottom: 2px
</style>
